if (!customElements.get('product-sticky-atc')) {
  customElements.define(
    'product-sticky-atc',
    class ProductStickyAtc extends HTMLElement {
      constructor() {
        super();
        this.expanded = false;
        this.syncing = false;
        this.loading = false;
        this.abortController = null;
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onOptionChange = this.onOptionChange.bind(this);
      }

      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.backdrop = this.querySelector('[data-sticky-atc-backdrop]');
        this.optionsPanel = this.querySelector('[data-sticky-atc-options]');
        this.mediaEl = this.querySelector('[data-sticky-atc-media]');
        this.priceCurrentEl = this.querySelector('[data-sticky-atc-price-current]');
        this.priceCompareEl = this.querySelector('[data-sticky-atc-price-compare]');
        this.priceCompareWrapEl = this.querySelector('[data-sticky-atc-price-compare-wrap]');
        this.buttons = Array.from(this.querySelectorAll('[data-sticky-atc-action]'));
        this.atcButton = this.querySelector('[data-sticky-atc-action="atc"]');
        this.buyButton = this.querySelector('[data-sticky-atc-action="buy"]');
        this.hasOptions = this.dataset.hasOptions === 'true';
        this.moneyFormat = this.dataset.moneyFormat || window.moneyFormat?.format || '${{amount}}';
        this.purchaseDisabled = false;

        this.buttons.forEach((button) => {
          button.addEventListener('click', (event) => this.onActionClick(event), { signal });
        });

        this.backdrop?.addEventListener('click', () => this.close({ manual: true }), { signal });
        this.querySelector('[data-sticky-atc-close]')?.addEventListener('click', () => this.close({ manual: true }), { signal });
        this.optionsPanel?.addEventListener('change', this.onOptionChange, { signal });

        document.addEventListener('keydown', this.onDocumentKeydown, { signal });
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        this.observeMainProduct();
        this.observeMainProductVisibility();
        this.syncFromMain();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.unsubscribeVariantChange?.();
        this.mainObserver?.disconnect();
        this.visibilityObserver?.disconnect();
      }

      get mainProductInfo() {
        return document.querySelector('product-info');
      }

      get mainSectionId() {
        return this.mainProductInfo?.dataset.section || '';
      }

      get mainPriceCurrentEl() {
        if (!this.mainProductInfo || !this.mainSectionId) return null;
        const priceWrap = this.mainProductInfo.querySelector(`#price-${this.mainSectionId}`);
        if (!priceWrap) return null;
        return (
          priceWrap.querySelector('.price__sale .price-item--sale') ||
          priceWrap.querySelector('.price__regular .price-item--regular')
        );
      }

      get mainPriceCompareEl() {
        if (!this.mainProductInfo || !this.mainSectionId) return null;
        const priceWrap = this.mainProductInfo.querySelector(`#price-${this.mainSectionId}`);
        if (!priceWrap) return null;
        return (
          priceWrap.querySelector('.price__sale s.price-item--regular') ||
          priceWrap.querySelector('.price__regular s.price-item--regular')
        );
      }

      get mainPriceCompareWrapEl() {
        if (!this.mainProductInfo || !this.mainSectionId) return null;
        const priceWrap = this.mainProductInfo.querySelector(`#price-${this.mainSectionId}`);
        if (!priceWrap) return null;
        return priceWrap.querySelector('.price__sale s.price-item--regular')?.closest('.price__sale');
      }

      get mainPicker() {
        return this.mainProductInfo?.querySelector('variant-selects');
      }

      get variantId() {
        const input = this.mainProductInfo?.querySelector(
          `#product-form-${this.mainSectionId} input[name="id"], .product-variant-id`
        );
        return input?.value || this.dataset.variantId || '';
      }

      get quantity() {
        const input = this.mainProductInfo?.querySelector('.quantity__input');
        const value = Number(input?.value);
        return Number.isFinite(value) && value > 0 ? value : 1;
      }

      get mainSubmitButton() {
        return (
          this.mainProductInfo?.querySelector(`#ProductSubmitButton-${this.mainSectionId}`) ||
          this.mainProductInfo?.querySelector('[type="submit"][name="add"]')
        );
      }

      onDocumentKeydown(event) {
        if (event.key === 'Escape' && this.expanded) {
          event.preventDefault();
          this.close({ manual: true });
        }
      }

      onActionClick(event) {
        const button = event.currentTarget;
        if (this.loading) return;

        if (!this.expanded && this.hasOptions) {
          this.lastTrigger = button;
          this.open();
          return;
        }

        if (this.purchaseDisabled || button.disabled) return;

        if (button.dataset.stickyAtcAction === 'buy') {
          this.buyNow(button);
          return;
        }

        this.addToCart();
      }

      setActionExpanded(expanded) {
        this.buttons.forEach((button) => {
          if (button.hasAttribute('aria-controls')) {
            button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
          }
        });
      }

      open() {
        if (!this.hasOptions || !this.optionsPanel) return;
        this.expanded = true;
        this.classList.add('is-open');
        this.setActionExpanded(true);
        this.syncButtonsFromMain();
        this.optionsPanel.hidden = false;
        if (this.backdrop) this.backdrop.hidden = false;
        const firstOption =
          this.optionsPanel.querySelector('input:not(.disabled):not(.visually-disabled)') ||
          this.optionsPanel.querySelector('input');
        firstOption?.focus();
      }

      close({ restoreFocus = true, manual = false, afterPurchase = false } = {}) {
        if (!manual && !afterPurchase) return;
        this.expanded = false;
        this.classList.remove('is-open');
        this.setActionExpanded(false);
        this.syncButtonsFromMain();
        if (this.optionsPanel) this.optionsPanel.hidden = true;
        if (this.backdrop) this.backdrop.hidden = true;
        if (restoreFocus) this.lastTrigger?.focus();
      }

      setBarVisible(visible) {
        this.classList.toggle('is-concealed', !visible);
        this.toggleAttribute('inert', !visible);
        if (visible) {
          this.removeAttribute('aria-hidden');
        } else {
          this.setAttribute('aria-hidden', 'true');
        }
      }

      observeMainProductVisibility() {
        this.visibilityObserver?.disconnect();
        const main = this.mainProductInfo;
        if (!main) {
          this.setBarVisible(true);
          return;
        }

        this.visibilityObserver = new IntersectionObserver(
          ([entry]) => {
            if (!entry) return;
            if (this.expanded) return;
            const pastMain = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
            this.setBarVisible(pastMain);
          },
          { threshold: 0 }
        );
        this.visibilityObserver.observe(main);
      }

      onOptionChange(event) {
        if (this.syncing) return;
        const input = event.target;
        if (!input || !input.matches('input[data-option-value-id]')) return;

        this.updateSelectedLabel(input);
        this.selectMainOption(input.dataset.optionValueId);
      }

      selectMainOption(optionValueId) {
        if (!optionValueId || !this.mainPicker) return;
        const mainInput = this.mainPicker.querySelector(`[data-option-value-id="${optionValueId}"]`);
        if (!mainInput) return;

        this.syncing = true;

        if (mainInput.tagName === 'OPTION') {
          const select = mainInput.closest('select');
          if (select && select.value !== mainInput.value) {
            select.value = mainInput.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else if (!mainInput.checked) {
          mainInput.click();
        }

        requestAnimationFrame(() => {
          this.syncing = false;
        });
      }

      onVariantChange({ data } = {}) {
        if (data?.sectionId && this.mainSectionId && data.sectionId !== this.mainSectionId) return;
        if (data?.variant) this.applyVariant(data.variant);
        this.syncFromMain();
      }

      observeMainProduct() {
        this.mainObserver?.disconnect();
        if (!this.mainProductInfo) return;

        this.mainObserver = new MutationObserver(() => {
          clearTimeout(this.syncTimer);
          this.syncTimer = setTimeout(() => this.syncFromMain(), 30);
        });
        this.mainObserver.observe(this.mainProductInfo, {
          attributes: true,
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      syncFromMain() {
        this.syncFromMainPicker();
        this.syncButtonsFromMain();
      }

      syncButtonsFromMain() {
        const submit = this.mainSubmitButton;
        const atcLabel = this.atcButton?.querySelector('span');
        const buyLabel = this.buyButton?.querySelector('span');
        const submitText = submit?.querySelector('span')?.textContent?.trim();
        const unavailable = Boolean(submit?.hasAttribute('disabled'));

        this.purchaseDisabled = unavailable;
        this.buttons.forEach((button) => {
          button.classList.toggle('is-unavailable', unavailable);
          if (this.loading) return;
          if (this.expanded && unavailable) {
            button.setAttribute('aria-disabled', 'true');
          } else {
            button.removeAttribute('aria-disabled');
          }
        });

        if (atcLabel && submitText) {
          atcLabel.textContent = submitText;
        } else if (atcLabel && unavailable) {
          atcLabel.textContent = window.variantStrings?.unavailable || atcLabel.textContent;
        } else if (atcLabel && this.atcButton?.dataset.label) {
          atcLabel.textContent = this.atcButton.dataset.label;
        }

        if (buyLabel && this.buyButton?.dataset.label) {
          buyLabel.textContent = this.buyButton.dataset.label;
        }
      }

      syncFromMainPicker() {
        if (!this.mainPicker) return;
        this.syncing = true;
        this.mainPicker.querySelectorAll('input[data-option-value-id]').forEach((mainInput) => {
          const stickyInput = this.querySelector(`input[data-option-value-id="${mainInput.dataset.optionValueId}"]`);
          if (!stickyInput) return;

          stickyInput.checked = mainInput.checked;
          const unavailable =
            mainInput.classList.contains('disabled') ||
            mainInput.classList.contains('visually-disabled') ||
            mainInput.disabled;
          stickyInput.classList.toggle('disabled', unavailable);
          stickyInput.classList.toggle('visually-disabled', unavailable);
          if (mainInput.checked) this.updateSelectedLabel(stickyInput);
        });
        this.mainPicker.querySelectorAll('select').forEach((select) => {
          const option = select.selectedOptions[0];
          const optionValueId = option?.dataset?.optionValueId;
          if (!optionValueId) return;
          const stickyInput = this.querySelector(`input[data-option-value-id="${optionValueId}"]`);
          if (stickyInput) {
            stickyInput.checked = true;
            this.updateSelectedLabel(stickyInput);
          }
        });
        this.syncing = false;
      }

      updateSelectedLabel(input) {
        const group = input.closest('[data-sticky-option]');
        const label = group?.querySelector('[data-selected-value]');
        if (label && input.value) label.textContent = input.value;
      }

      applyVariant(variant) {
        if (!variant) return;

        this.dataset.variantId = variant.id || '';
        this.updatePrice(variant);
        this.updateImage(variant);
      }

      formatMoney(cents) {
        if (typeof currencyFormatter !== 'undefined' && currencyFormatter.formats?.formatMoney) {
          return currencyFormatter.formats.formatMoney(cents, this.moneyFormat);
        }
        const amount = Number(cents || 0) / 100;
        return `$${amount.toFixed(2)}`;
      }

      syncMoneyElement(target, { source = null, cents = null } = {}) {
        if (!target) return;

        if (window.themeMoneyDom?.sync) {
          window.themeMoneyDom.sync(target, {
            source,
            cents,
            moneyFormat: this.moneyFormat,
          });
          return;
        }

        if (source) {
          target.innerHTML = source.innerHTML;
          return;
        }

        target.innerHTML = this.formatMoney(cents);
      }

      updatePrice(variant) {
        const mainCurrent = this.mainPriceCurrentEl;
        const mainCompare = this.mainPriceCompareEl;
        const showCompare =
          Number(variant?.compare_at_price || 0) > Number(variant?.price || 0);

        this.syncMoneyElement(this.priceCurrentEl, {
          source: mainCurrent,
          cents: variant?.price,
        });

        if (!this.priceCompareEl) return;

        if (showCompare) {
          this.syncMoneyElement(this.priceCompareEl, {
            source: mainCompare,
            cents: variant?.compare_at_price,
          });
        } else {
          if (window.themeMoneyDom?.clear) {
            window.themeMoneyDom.clear(this.priceCompareEl);
          } else {
            this.priceCompareEl.innerHTML = '';
          }
        }
        this.priceCompareWrapEl?.classList.toggle('hidden', !showCompare);
      }

      updateImage(variant) {
        const image = variant.featured_image;
        const src = image?.src || image?.url;
        if (!src || !this.mediaEl) return;
        let img = this.mediaEl.querySelector('img');
        if (!img) {
          img = document.createElement('img');
          img.className = 'product-sticky-atc__image';
          this.mediaEl.replaceChildren(img);
        }
        img.alt = this.dataset.productTitle || '';
        img.src = src;
        img.removeAttribute('srcset');
      }

      addToCart() {
        if (this.purchaseDisabled) return;

        const submit =
          this.mainProductInfo?.querySelector(`#ProductSubmitButton-${this.mainSectionId}`) ||
          this.mainProductInfo?.querySelector('[type="submit"][name="add"]');

        if (submit && !submit.disabled) {
          submit.click();
          this.close({ afterPurchase: true });
          return;
        }
      }

      async buyNow(button) {
        await this.addThenCheckout(true, button);
      }

      async addThenCheckout(checkout, button) {
        if (this.purchaseDisabled) return;
        const variantId = this.variantId;
        if (!variantId) return;

        this.setLoading(button, true);

        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', String(this.quantity));

          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          if (cart && !checkout && typeof cart.getSectionsToRender === 'function') {
            formData.append(
              'sections',
              cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            cart.setActiveElement?.(document.activeElement);
          }

          config.body = formData;

          const response = await fetch(`${window.routes.cart_add_url}`, config);
          const data = await response.json();

          if (data.status) {
            throw new Error(data.description || data.message || 'Add to cart failed');
          }

          if (checkout) {
            window.location = '/checkout';
            return;
          }

          if (cart?.renderContents) {
            cart.renderContents(data);
          } else {
            window.location = window.routes.cart_url;
          }
          this.close({ afterPurchase: true });
        } catch (error) {
          console.error(error);
        } finally {
          this.setLoading(button, false);
        }
      }

      setLoading(button, isLoading) {
        this.loading = isLoading;
        if (!button) return;
        button.classList.toggle('loading', isLoading);
        button.toggleAttribute('aria-busy', isLoading);
        if (isLoading) {
          button.setAttribute('aria-disabled', 'true');
        } else if (this.expanded && this.purchaseDisabled) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
        const spinner = button.querySelector('.loading__spinner');
        spinner?.classList.toggle('hidden', !isLoading);
      }
    }
  );
}
